import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '@shared/supabase/client';
import './tour.css';

// A dependency-free guided tour: spotlights real UI elements (by a `data-tour`
// selector) with a dimmed cutout mask + a positioned tooltip. Robust on mobile:
// it can reveal a collapsed menu (`reveal`), retries locating a target, and
// auto-skips a step whose target never appears (e.g. a feature-gated nav item).
export type TourStep = {
  /** CSS selector of the element to spotlight. Omit for a centered card (welcome/final). */
  target?: string;
  /** Selector of a control to click when the target is hidden (e.g. a mobile hamburger). */
  reveal?: string;
  title: string;
  body: string;
};

export type TourLabels = { skip: string; back: string; next: string; done: string };

type Rect = { x: number; y: number; w: number; h: number } | null;

function isVisible(el: Element | null): el is HTMLElement {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0 && getComputedStyle(el as HTMLElement).visibility !== 'hidden';
}

function TourOverlay({
  steps, index, setIndex, onClose, labels,
}: {
  steps: TourStep[]; index: number; setIndex: (n: number) => void; onClose: () => void; labels: TourLabels;
}) {
  const step = steps[index];
  const [rect, setRect] = useState<Rect>(null);
  const [pos, setPos] = useState<{ top: number; left: number; place: 'top' | 'bottom' | 'center' }>({
    top: 0, left: 0, place: 'center',
  });
  const cardRef = useRef<HTMLDivElement>(null);
  const PAD = 6;
  const isLast = index === steps.length - 1;

  // Locate + measure the target, revealing a collapsed menu and retrying for ~800ms.
  useEffect(() => {
    if (!step.target) { setRect(null); return; }
    let timer = 0;
    let tries = 0;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      let el = document.querySelector(step.target!);
      if ((!el || !isVisible(el)) && step.reveal) {
        const rev = document.querySelector(step.reveal) as HTMLElement | null;
        if (rev && rev.getAttribute('aria-expanded') !== 'true') rev.click();
      }
      el = document.querySelector(step.target!);
      if (el && isVisible(el)) {
        try { el.scrollIntoView({ block: 'center', behavior: 'auto' }); } catch { /* ignore */ }
        const r = el.getBoundingClientRect();
        setRect({ x: r.left, y: r.top, w: r.width, h: r.height });
        return;
      }
      tries += 1;
      if (tries > 24) {
        // Target never showed up (e.g. gated feature) — skip forward, or finish.
        if (index < steps.length - 1) setIndex(index + 1); else onClose();
        return;
      }
      timer = window.setTimeout(tick, 34);
    };
    tick();
    return () => { cancelled = true; window.clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, step.target, step.reveal, steps.length]);

  // Position the tooltip relative to the spotlight (below if it fits, else above; centered when no target).
  useLayoutEffect(() => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const card = cardRef.current;
    const cw = card?.offsetWidth ?? 300;
    const ch = card?.offsetHeight ?? 180;
    if (!rect) {
      setPos({ top: Math.max(16, (vh - ch) / 2), left: Math.max(16, (vw - cw) / 2), place: 'center' });
      return;
    }
    const gap = 14;
    let place: 'top' | 'bottom' = 'bottom';
    let top = rect.y + rect.h + gap;
    if (top + ch > vh - 12) { place = 'top'; top = rect.y - ch - gap; }
    if (top < 12) top = 12;
    let left = rect.x + rect.w / 2 - cw / 2;
    left = Math.min(Math.max(12, left), vw - cw - 12);
    setPos({ top, left, place });
  }, [rect, index]);

  // Keep the spotlight glued to the target on scroll/resize.
  useEffect(() => {
    if (!step.target) return;
    const on = () => {
      const el = document.querySelector(step.target!);
      if (el && isVisible(el)) {
        const r = el.getBoundingClientRect();
        setRect({ x: r.left, y: r.top, w: r.width, h: r.height });
      }
    };
    window.addEventListener('resize', on);
    window.addEventListener('scroll', on, true);
    return () => {
      window.removeEventListener('resize', on);
      window.removeEventListener('scroll', on, true);
    };
  }, [step.target]);

  // Esc closes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div className="tour-root" role="dialog" aria-modal="true" aria-label={step.title}>
      <svg className="tour-mask" width="100%" height="100%" aria-hidden="true">
        <defs>
          <mask id="tour-hole">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {rect && (
              <rect x={rect.x - PAD} y={rect.y - PAD} width={rect.w + PAD * 2} height={rect.h + PAD * 2} rx="12" fill="black" />
            )}
          </mask>
        </defs>
        <rect x="0" y="0" width="100%" height="100%" fill="rgba(4,7,13,0.80)" mask="url(#tour-hole)" />
        {rect && (
          <rect className="tour-ring" x={rect.x - PAD} y={rect.y - PAD} width={rect.w + PAD * 2} height={rect.h + PAD * 2} rx="12" />
        )}
      </svg>

      <div ref={cardRef} className={`tour-card tour-${pos.place}`} style={{ top: pos.top, left: pos.left }}>
        <div className="tour-progress">{index + 1} / {steps.length}</div>
        <div className="tour-title">{step.title}</div>
        <div className="tour-body">{step.body}</div>
        <div className="tour-actions">
          <button type="button" className="tour-skip" onClick={onClose}>{labels.skip}</button>
          <div className="tour-navbtns">
            {index > 0 && (
              <button type="button" className="tour-back" onClick={() => setIndex(index - 1)}>{labels.back}</button>
            )}
            <button type="button" className="tour-next" onClick={() => (isLast ? onClose() : setIndex(index + 1))}>
              {isLast ? labels.done : labels.next}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Mounts a guided tour. Auto-starts once per (id) — "seen" lives on the account
 * (auth user_metadata.tours_seen), with localStorage only as a fast local cache.
 * localStorage alone re-showed the tour in every new browser, device, private
 * window and on localhost. `start()` replays it (wire to a "?" button).
 */
export function useTour({
  id, steps, enabled, labels,
}: {
  id: string; steps: TourStep[]; enabled: boolean; labels: TourLabels;
}) {
  const [run, setRun] = useState(false);
  const [i, setI] = useState(0);
  const doneKey = useMemo(() => `adamar_tour_${id}`, [id]);

  const markSeen = useCallback(async () => {
    try { localStorage.setItem(doneKey, '1'); } catch { /* ignore */ }
    const { data } = await supabase.auth.getSession();
    const meta = (data.session?.user?.user_metadata ?? {}) as { tours_seen?: Record<string, string> };
    if (meta.tours_seen?.[id]) return;
    await supabase.auth.updateUser({ data: { tours_seen: { ...(meta.tours_seen ?? {}), [id]: new Date().toISOString() } } });
  }, [doneKey, id]);

  useEffect(() => {
    if (!enabled || run || steps.length === 0) return;
    try { if (localStorage.getItem(doneKey)) return; } catch { /* ignore */ }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    void supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;
      const seen = (data.session?.user?.user_metadata as { tours_seen?: Record<string, string> } | undefined)?.tours_seen?.[id];
      if (seen) { try { localStorage.setItem(doneKey, '1'); } catch { /* ignore */ } return; }
      timer = setTimeout(() => {
        // Mark seen the MOMENT it shows (not only on close), so a reload before
        // dismissing doesn't bring it back.
        void markSeen();
        setI(0); setRun(true);
      }, 900);
    });
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [enabled, run, steps.length, doneKey, id, markSeen]);

  const close = useCallback(() => {
    setRun(false);
    void markSeen();
  }, [markSeen]);

  const start = useCallback(() => { setI(0); setRun(true); }, []);

  const render = run && steps.length > 0
    ? <TourOverlay steps={steps} index={Math.min(i, steps.length - 1)} setIndex={setI} onClose={close} labels={labels} />
    : null;

  return { render, start };
}
